import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, MouseEvent } from 'react'
import { Button, Checkbox, Divider, IconButton, Popover } from '@mui/material'

import Chip from '../common/chip'
import { Document } from '../../types/document.types'
import PlayIcon from '../../assets/icons/play-icon.svg'
import StopIcon from '../../assets/icons/stop-icon.svg'
import { UserSessionType } from '../../types/user.types'
import TrashIcon from '../../assets/icons/trash-icon.svg'
import AccountIcon from '../../assets/icons/account-icon.svg'
import MoreMenuIcon from '../../assets/icons/more-menu-icon.svg'
import ArrowRightIcon from '../../assets/icons/arrow-right-icon.svg'
import BankFindingIcon from '../../assets/icons/bank-finding-icon.svg'
import { baseButtonCancel, baseButtonBlueBg } from '../../theme/custom-styles'

const DocumentsToReviewListItem = ({
  userSession,
  documentData,
  processingDocument,
  handleGoToDocumentDetails,
  handleToggleDocumentSelection,
  handleOpenStopDocumentDataModal,
  handleOpenResumeDocumentDataModal,
  handleOpenDeleteDocumentDataModal
}: {
  documentData: Document
  processingDocument: boolean
  userSession: Partial<UserSessionType>
  handleGoToDocumentDetails: () => void
  handleOpenStopDocumentDataModal: (selectedDocumentData: Document) => void
  handleOpenResumeDocumentDataModal: (selectedDocumentData: Document) => void
  handleOpenDeleteDocumentDataModal: (selectedDocumentData: Document) => void
  handleToggleDocumentSelection: (selectedDocumentData: Document, selected: boolean) => void
}) => {
  const { t } = useTranslation(['documents-to-review-management'])

  const [noveltiesCount, setNoveltiesCount] = useState(0)
  const [documentFileExt, setDocumentFileExt] = useState('')
  const [documentChipStatus, setDocumentChipStatus] = useState('')
  // const [uploadedByFullName, setUploadedByFullName] = useState('')
  const [documentChipStatusText, setDocumentChipStatusText] = useState('')
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [documentNameWithoutFileExt, setDocumentNameWithoutFileExt] = useState('')

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  useEffect(() => {
    if (documentData) {
      let documentNameWithoutFileExtData = ''
      const documentNameWithoutFileExtArray = documentData.originalName.split('.')

      for (let i = 0; i < documentNameWithoutFileExtArray.length - 1; i++) {
        documentNameWithoutFileExtData += documentNameWithoutFileExtArray[i]
      }

      setDocumentNameWithoutFileExt(documentNameWithoutFileExtData)
      setDocumentFileExt(documentNameWithoutFileExtArray[documentNameWithoutFileExtArray.length - 1])

      if (documentData.status === 'analysis-completed') {
        handleCheckNoveltiesCount(documentData)
      }
    }

    setDocumentChipStatus(() => {
      if (documentData.status === 'uploaded' || documentData.status === 'analyzing' || documentData.status === 'downloaded' || documentData.status === 'downloaded-n-analyzing') {
        return 'analyzing'
      }

      if (documentData.status.indexOf('error') !== -1) {
        return 'error'
      }

      if (documentData.status === 'stopped') {
        return 'disabled'
      }

      return documentData.status
    })

    setDocumentChipStatusText(() => {
      if (documentData.status === 'uploaded' || documentData.status === 'analyzing' || documentData.status === 'downloaded' || documentData.status === 'downloaded-n-analyzing') {
        return t('listItem.chipStatusTexts.analyzing')
      }

      if (documentData.status === 'error-downloading-file') {
        return t('listItem.chipStatusTexts.downloadError')
      }

      if (documentData.status === 'error-analyzing-file') {
        return t('listItem.chipStatusTexts.fileError')
      }

      if (documentData.status === 'stopped') {
        return t('listItem.chipStatusTexts.stoppedAnalysis')
      }

      if (documentData.status === 'error') {
        return t('listItem.chipStatusTexts.cobreError')
      }

      if (documentData.status === 'analysis-completed') {
        return t('listItem.chipStatusTexts.completedAnalysis')
      }

      return t('listItem.chipStatusTexts.inProcess')
    })
  }, [documentData, t])

  const handleCheckNoveltiesCount = (documentData: Document) => {
    setNoveltiesCount(
      documentData.totalOfNoveltiesEmptyBankName +
        documentData.totalOfNoveltiesUnknownBankName +
        documentData.totalOfNoveltiesWrongDocumentType +
        documentData.totalOfNoveltiesBackgroundResearch +
        documentData.totalOfNoveltiesEmptyPaymentAmount +
        documentData.totalOfNoveltiesWrongPaymentAmount +
        documentData.totalOfNoveltiesEmptyRegisteredName +
        documentData.totalOfNoveltiesEmptyDocumentNumber +
        documentData.totalOfNoveltiesWrongDocumentNumber +
        documentData.totalOfNoveltiesEmptyPaymentReference +
        documentData.totalOfNoveltiesEmptyBankAccountNumber +
        documentData.totalOfNoveltiesWrongBankAccountNumber +
        documentData.totalOfNoveltiesDuplicatePaymentReference +
        documentData.totalOfNoveltiesDuplicateBankAccountNumber +
        documentData.totalOfNoveltiesBankAccountNumberNotRegistered +
        documentData.totalOfNoveltiesWrongBankAccountType +
        documentData.totalOfNoveltiesEmptyBankAccountType
    )
  }

  return (
    <>
      <div
        onClick={() => {
          if (documentData.status !== 'uploaded' && documentData.status !== 'analyzing' && documentData.status !== 'downloaded' && documentData.status !== 'stopped' && documentData.status !== 'downloaded-n-analyzing') {
            handleGoToDocumentDetails()
          }
        }}
        className="documents-list-item-data-container"
      >
        <div className="documents-list-item-data-document-info">
          {processingDocument === false && (
            <Checkbox
              checked={documentData.selected ?? false}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                handleToggleDocumentSelection(documentData, event.target.checked)
              }}
            />
          )}
          <div className="documents-list-item-data-document-info-name">{`${documentNameWithoutFileExt.toUpperCase()}.${documentFileExt}`}</div>
        </div>
        {documentData.status === 'analysis-completed' ? (
          <div className="documents-list-item-uploaded-by-container">
            <div className="documents-list-item-bank-account-finding-icon-container">
              <img
                src={BankFindingIcon}
                alt="bank-account-finding-icon"
              />
            </div>
            <div className="documents-list-item-findings-text">
              <div>{`${Number(noveltiesCount)} ${Number(noveltiesCount) === 1 ? t('listItem.noveltyText') : t('listItem.noveltiesText')} ${t('listItem.toReviewText')}`}</div>
            </div>
          </div>
        ) : (
          <div className="documents-list-item-uploaded-by-container">
            <div className="documents-list-item-uploaded-by-icon-container">
              <img
                src={AccountIcon}
                alt="account-icon"
              />
            </div>
            <div className="documents-list-item-uploaded-by-text">
              <strong>{t('listItem.uploadedBy')}</strong>
              <div>{documentData.uploadedBy && typeof documentData.uploadedByFullName === 'string' ? `${documentData.uploadedByFullName?.toUpperCase()}` : ''}</div>
            </div>
          </div>
        )}
        <div className="documents-list-item-status-container">
          <Chip
            status={documentChipStatus}
            label={documentChipStatusText}
          />
        </div>
        <div className="documents-list-item-actions-container">
          <IconButton
            edge="end"
            sx={{ color: '#107569' }}
            onClick={async (event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()

              // if (!uploadedByFullName) {
              //   const uploadedByFullNameToSet = await handleGetUploadedByFullName(documentData)
              //   setUploadedByFullName(uploadedByFullNameToSet)
              // }

              setAnchorEl(event.currentTarget)
            }}
            aria-label="toggle password visibility"
          >
            <img
              src={MoreMenuIcon}
              alt="more-menu-icon"
            />
          </IconButton>
          {processingDocument === false && (
            <>
              {((documentData.status === 'analysis-completed' && userSession?.role === 'admin') ||
                (documentData.status !== 'uploaded' &&
                  documentData.status !== 'analyzing' &&
                  documentData.status !== 'downloaded' &&
                  documentData.status !== 'downloaded-n-analyzing' &&
                  documentData.status !== 'analysis-completed')) && (
                <IconButton
                  edge="end"
                  sx={{ color: '#107569' }}
                  onClick={(event) => {
                    event.stopPropagation()
                    handleOpenDeleteDocumentDataModal(documentData)
                  }}
                  aria-label="toggle password visibility"
                >
                  <img
                    src={TrashIcon}
                    alt="trash-icon"
                  />
                </IconButton>
              )}
            </>
          )}
          <div className="documents-list-item-arrow-right">
            <img
              src={ArrowRightIcon}
              alt="arrow-right-icon"
            />
          </div>
        </div>
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation()
          setAnchorEl(null)
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <div className="documents-list-item-popover-container">
          <div className="documents-list-item-popover">
            <strong>{t('listItem.uploadedBy')}</strong>
            <div>{documentData.uploadedByFullName}</div>
            <div>{format(documentData.createdAt, 'dd/MM/yyyy | HH:mm:ss')}</div>
          </div>
          {documentData.status === 'downloaded-n-analyzing' && (
            <>
              <Divider />
              <div className="documents-list-item-popover">
                <Button
                  fullWidth
                  variant="text"
                  sx={baseButtonCancel}
                  startIcon={
                    <img
                      src={StopIcon}
                      alt="stop-icon"
                    />
                  }
                  className="documents-actions-bar-button-action"
                  onClick={() => {
                    setAnchorEl(null)
                    handleOpenStopDocumentDataModal(documentData)
                  }}
                >
                  <div className="documents-actions-bar-button-action-text">{t('listItem.buttonsTexts.stopAnalysis')}</div>
                </Button>
              </div>
            </>
          )}
          {documentData.status === 'stopped' && (
            <>
              <Divider />
              <div className="documents-list-item-popover">
                <Button
                  fullWidth
                  variant="text"
                  sx={{
                    ...baseButtonBlueBg,
                    '&.MuiButtonBase-root:hover .MuiButton-startIcon': {
                      filter: 'brightness(0) invert(1)'
                    }
                  }}
                  startIcon={
                    <img
                      src={PlayIcon}
                      alt="play-icon"
                    />
                  }
                  className="documents-actions-bar-button-action"
                  onClick={() => {
                    setAnchorEl(null)
                    handleOpenResumeDocumentDataModal(documentData)
                  }}
                >
                  <div className="documents-actions-bar-button-action-text">{t('listItem.buttonsTexts.resumeAnalysis')}</div>
                </Button>
              </div>
            </>
          )}
        </div>
      </Popover>
    </>
  )
}

export default DocumentsToReviewListItem
